// Thin client for apps/api. In dev (or when VITE_API_BASE is unset) all calls
// fall through to a local stub so `pnpm dev` lights up end-to-end without a
// running Worker. The dev stub:
//   - /gen   → returns a Pollinations URL + provider chip "Pollinations (dev stub)"
//   - /anchor/last → returns a fixture Amoy tx
//
// Wire format mirrors the API15 spec:
//   POST /gen          → { image_url, provider: { kind, agent_id? } }
//   GET  /anchor/last  → { tx_hash, block, root, chain, explorer_url, timestamp_ms }

const API_BASE = (import.meta.env.VITE_API_BASE ?? "").trim();

/** Same-origin when VITE_API_BASE is unset (prod Pages routes /api/* to Worker). */
export const apiBase = API_BASE;

import { createSignal } from "solid-js";

/** API build-stamp (`v{ver}·{sha}·{date}`) — set by any response that carries
 * the `x-api-build` header. The footer renders this next to the web stamp so
 * the operator can verify alignment without leaving the page. */
const [apiBuildStamp, setApiBuildStamp] = createSignal<string>("");
export { apiBuildStamp };
function captureApiBuild(res: Response): void {
  const stamp = res.headers.get("x-api-build");
  if (stamp && stamp !== apiBuildStamp()) setApiBuildStamp(stamp);
}

export type ProviderKind = "oracle" | "openrouter" | "pollinations" | "pollinations-stub";

export interface Provider {
  kind: ProviderKind;
  agentId?: string;
}

export interface GenerateResponse {
  imageUrl: string;
  provider: Provider;
}

export interface LastAnchor {
  txHash: string;
  block: number | null;
  explorerUrl: string;
  /** Unix ms when the anchor sealed. `0` means "no real anchor yet" — used
   *  by the footer to decide whether to show the "sealed N min ago" pill. */
  timestampMs: number;
}

const FIXTURE_ANCHOR: LastAnchor = {
  txHash: "0xdeadbeefcafe0000000000000000000000000000000000000000000000000000",
  block: null,
  explorerUrl: "https://amoy.polygonscan.com",
  timestampMs: 0,
};

/** True iff `a` represents a real sealed anchor (vs. the placeholder fixture). */
export function isRealAnchor(a: LastAnchor | null): boolean {
  return !!a && a.block !== null && a.timestampMs > 0;
}

function pollinationsStub(prompt: string): GenerateResponse {
  const safe = encodeURIComponent(prompt.slice(0, 280));
  const seed = Math.floor(Math.random() * 1_000_000);
  return {
    imageUrl: `https://image.pollinations.ai/prompt/${safe}?width=512&height=512&seed=${seed}&nologo=true`,
    provider: { kind: "pollinations-stub" },
  };
}

export async function generate(prompt: string): Promise<GenerateResponse> {
  if (!API_BASE) return pollinationsStub(prompt);
  try {
    const res = await fetch(`${API_BASE}/gen`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ prompt }),
    });
    if (!res.ok) return pollinationsStub(prompt);
    const data = (await res.json()) as { image_url?: string; provider?: { kind?: string; agent_id?: string } };
    if (!data.image_url || !data.provider?.kind) return pollinationsStub(prompt);
    return {
      imageUrl: data.image_url,
      provider: { kind: data.provider.kind as ProviderKind, agentId: data.provider.agent_id },
    };
  } catch {
    return pollinationsStub(prompt);
  }
}

export async function fetchLastAnchor(): Promise<LastAnchor> {
  if (!API_BASE) return FIXTURE_ANCHOR;
  try {
    const res = await fetch(`${API_BASE}/anchor/last`);
    if (!res.ok) return FIXTURE_ANCHOR;
    const data = (await res.json()) as Record<string, unknown>;
    return {
      txHash: String(data.tx_hash ?? FIXTURE_ANCHOR.txHash),
      block: data.block == null ? null : Number(data.block),
      explorerUrl: String(data.explorer_url ?? FIXTURE_ANCHOR.explorerUrl),
      timestampMs: typeof data.timestamp_ms === "number" ? data.timestamp_ms : 0,
    };
  } catch {
    return FIXTURE_ANCHOR;
  }
}

export type ProvenanceVerdict =
  | "cdn-trusted"
  | "c2pa-verified"
  | "synthid-detected"
  | "first-party"
  | "unverified";

export interface ImportUrlResponse {
  pieceId: string;
  imageUrl: string;
  providerId: string;
  modStatus: string;
  provenanceVerdict: ProvenanceVerdict;
  /** Server returned 409 `duplicate_image`: this exact image already exists on the wall. */
  duplicate?: boolean;
}

export type ImportUrlErrorCode =
  | "provider_not_allowed"
  | "not_an_image"
  | "unsupported_image_format"
  | "ssrf_blocked"
  | "rate_limited"
  | "upstream_oversize"
  | "upstream_fetch_failed"
  | "invalid_url"
  | "invalid_body"
  | "anon_uid_failed"
  | "storage_failed"
  | "network_error"
  | "unknown";

export class ImportUrlError extends Error {
  code: ImportUrlErrorCode;
  status: number;
  constructor(code: ImportUrlErrorCode, status: number, message?: string) {
    super(message ?? code);
    this.code = code;
    this.status = status;
  }
}

/**
 * POST /pieces/import-url — server pulls the image, scans it, lands it in R2.
 * Uses `credentials: "include"` so the browser sends the HttpOnly anon cookie.
 */
export async function importUrl(sourceUrl: string): Promise<ImportUrlResponse> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/pieces/import-url`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ source_url: sourceUrl }),
    });
  } catch (err) {
    throw new ImportUrlError("network_error", 0, err instanceof Error ? err.message : String(err));
  }

  let body: Record<string, unknown> = {};
  try {
    body = (await res.json()) as Record<string, unknown>;
  } catch {
    // empty / non-JSON body — handled below by status check
  }

  if (res.status === 409 && body.error === "duplicate_image") {
    return {
      pieceId: String(body.piece_id ?? ""),
      imageUrl: String(body.image_url ?? ""),
      providerId: "",
      modStatus: "cleared",
      provenanceVerdict: "unverified",
      duplicate: true,
    };
  }

  if (!res.ok) {
    const code = (typeof body.error === "string" ? body.error : "unknown") as ImportUrlErrorCode;
    throw new ImportUrlError(code, res.status);
  }

  return {
    pieceId: String(body.piece_id ?? ""),
    imageUrl: String(body.image_url ?? ""),
    providerId: String(body.provider_id ?? ""),
    modStatus: String(body.mod_status ?? "pending"),
    provenanceVerdict: (body.provenance_verdict as ProvenanceVerdict) ?? "unverified",
  };
}

/**
 * POST /pieces/import-file — direct bytes upload (escape hatch for hosts that
 * block server-side fetching, like Gemini). Body is the raw image bytes;
 * Content-Type drives the extension.
 */
export async function importFile(blob: Blob): Promise<ImportUrlResponse> {
  const contentType = blob.type || "application/octet-stream";
  if (!contentType.startsWith("image/")) {
    throw new ImportUrlError("not_an_image", 0, `file type: ${contentType}`);
  }
  if (blob.size === 0) {
    throw new ImportUrlError("invalid_body", 0, "empty file");
  }
  if (blob.size > 10 * 1024 * 1024) {
    throw new ImportUrlError("upstream_oversize", 0, "file > 10 MB");
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/pieces/import-file`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": contentType },
      body: blob,
    });
  } catch (err) {
    throw new ImportUrlError("network_error", 0, err instanceof Error ? err.message : String(err));
  }

  let body: Record<string, unknown> = {};
  try {
    body = (await res.json()) as Record<string, unknown>;
  } catch {
    // empty / non-JSON body — handled below
  }

  // 409 `duplicate_image` is a *success* shape: the bytes are already on the
  // wall, server returns the existing piece_id + image_url so the client
  // can deep-link instead of uploading. Caller should show a "ya en el muro"
  // banner on top of the preview.
  if (res.status === 409 && body.error === "duplicate_image") {
    return {
      pieceId: String(body.piece_id ?? ""),
      imageUrl: String(body.image_url ?? ""),
      providerId: "",
      modStatus: "cleared",
      provenanceVerdict: "unverified",
      duplicate: true,
    };
  }

  if (!res.ok) {
    const code = (typeof body.error === "string" ? body.error : "unknown") as ImportUrlErrorCode;
    throw new ImportUrlError(code, res.status);
  }

  return {
    pieceId: String(body.piece_id ?? ""),
    imageUrl: String(body.image_url ?? ""),
    providerId: String(body.provider_id ?? ""),
    modStatus: String(body.mod_status ?? "pending"),
    provenanceVerdict: (body.provenance_verdict as ProvenanceVerdict) ?? "unverified",
  };
}

export interface PieceSocials {
  x?: string;
  twitter?: string;
  instagram?: string;
  telegram?: string;
  web?: string;
}

export interface PieceListItem {
  id: string;
  imageUrl: string;
  thumbUrl: string;
  mediumUrl: string;
  provenanceVerdict: ProvenanceVerdict;
  kind: "real" | "demo";
  authorNickname?: string;
  authorBrand?: string;
  authorSocials?: PieceSocials;
  /** Derived from (x,y)==(0,0). `nucleus` only at the centre. */
  displayZone?: "nucleus" | "oort";
  /** Unix seconds. Server-authored. */
  createdAt?: number;
  likes: number;
  dislikes: number;
  /** Only set when the caller has identity. Cold visitors see `undefined`. */
  myVote?: -1 | 0 | 1;
  /** Server-authoritative grid coordinate (integer cell x). */
  x?: number;
  /** Server-authoritative grid coordinate (integer cell y). */
  y?: number;
  /** True if this piece has ever occupied the centre cell (0,0). */
  wasCentre?: boolean;
  /** True if the requesting user created this piece. */
  isOwn?: boolean;
}

export interface ViewportWindow {
  cx: number;
  cy: number;
  r: number;
  truncated: boolean;
}

export interface PieceListResponse {
  pieces: PieceListItem[];
  window: ViewportWindow;
}

export interface PieceDetail {
  piece: PieceListItem;
  prevId: string | null;
  nextId: string | null;
}

export interface VoteResponse {
  pieceId: string;
  likes: number;
  dislikes: number;
  myVote: -1 | 0 | 1;
}

function parseSocials(raw: unknown): PieceSocials | undefined {
  if (typeof raw !== "string" || raw.length === 0) return undefined;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: PieceSocials = {};
    for (const k of ["x", "twitter", "instagram", "telegram", "web"] as const) {
      const v = parsed[k];
      if (typeof v === "string" && v.length > 0) out[k] = v;
    }
    return Object.keys(out).length > 0 ? out : undefined;
  } catch {
    return undefined;
  }
}

function mapLayer(z: unknown): "nucleus" | "oort" | undefined {
  if (z === "nucleus") return "nucleus";
  if (z === "oort") return "oort";
  return undefined;
}

function projectTile(t: Record<string, unknown>): PieceListItem {
  const kind = t.kind === "demo" ? "demo" : "real";
  const imageUrl = typeof t.image_url === "string" ? t.image_url : "";
  const myVoteRaw = typeof t.my_vote === "number" ? t.my_vote : undefined;
  const myVote: -1 | 0 | 1 | undefined =
    myVoteRaw === -1 || myVoteRaw === 0 || myVoteRaw === 1 ? (myVoteRaw as -1 | 0 | 1) : undefined;
  return {
    id: typeof t.id === "string" ? t.id : "",
    imageUrl,
    thumbUrl: typeof t.thumb_url === "string" ? t.thumb_url : imageUrl,
    mediumUrl: typeof t.medium_url === "string" ? t.medium_url : imageUrl,
    provenanceVerdict:
      typeof t.provenance_verdict === "string"
        ? (t.provenance_verdict as ProvenanceVerdict)
        : ("unverified" as ProvenanceVerdict),
    kind,
    authorNickname: typeof t.author_nickname === "string" ? t.author_nickname : undefined,
    authorBrand: typeof t.author_brand === "string" ? t.author_brand : undefined,
    authorSocials: parseSocials(t.author_socials_json),
    displayZone: mapLayer(t.layer),
    createdAt: typeof t.created_at === "number" ? t.created_at : undefined,
    likes: typeof t.likes === "number" ? t.likes : 0,
    dislikes: typeof t.dislikes === "number" ? t.dislikes : 0,
    x: typeof t.x === "number" ? t.x : undefined,
    y: typeof t.y === "number" ? t.y : undefined,
    myVote,
    wasCentre: t.was_centre === true || t.was_centre === 1,
    isOwn: t.is_own === true || t.is_own === 1,
  };
}

export interface ViewportQuery {
  cx: number;
  cy: number;
  r: number;
}

const EMPTY_WINDOW: ViewportWindow = { cx: 0, cy: 0, r: 0, truncated: false };

/**
 * v9 viewport-bounded list. Sends `(cx, cy, r)` in cell units; the server
 * returns only pieces inside the bbox (capped at 1000). When `view` is
 * omitted the server defaults to (0, 0, DEFAULT_R) — that's the cold-load
 * default centred on the heart.
 */
export async function fetchPieces(view?: ViewportQuery): Promise<PieceListResponse> {
  if (!API_BASE) return { pieces: [], window: EMPTY_WINDOW };
  try {
    let url = `${API_BASE}/pieces`;
    if (view) {
      const p = new URLSearchParams();
      p.set("cx", Math.round(view.cx).toString());
      p.set("cy", Math.round(view.cy).toString());
      p.set("r", Math.max(1, Math.round(view.r)).toString());
      url += `?${p.toString()}`;
    }
    const res = await fetch(url, { credentials: "include" });
    captureApiBuild(res);
    if (!res.ok) return { pieces: [], window: EMPTY_WINDOW };
    const data = (await res.json()) as {
      tiles?: Array<Record<string, unknown>>;
      window?: { cx?: number; cy?: number; r?: number; truncated?: boolean };
    };
    const tiles = Array.isArray(data.tiles) ? data.tiles : [];
    const pieces = tiles.map(projectTile).filter((p) => p.id && p.imageUrl);
    const w = data.window ?? {};
    return {
      pieces,
      window: {
        cx: typeof w.cx === "number" ? w.cx : 0,
        cy: typeof w.cy === "number" ? w.cy : 0,
        r: typeof w.r === "number" ? w.r : 0,
        truncated: w.truncated === true,
      },
    };
  } catch {
    return { pieces: [], window: EMPTY_WINDOW };
  }
}

export async function fetchPieceDetail(id: string): Promise<PieceDetail | null> {
  if (!API_BASE || !id) return null;
  try {
    const res = await fetch(`${API_BASE}/pieces/${encodeURIComponent(id)}`, {
      credentials: "include",
    });
    if (!res.ok) return null;
    const data = (await res.json()) as {
      piece?: Record<string, unknown>;
      prev_id?: string | null;
      next_id?: string | null;
    };
    if (!data.piece) return null;
    return {
      piece: projectTile(data.piece),
      prevId: typeof data.prev_id === "string" ? data.prev_id : null,
      nextId: typeof data.next_id === "string" ? data.next_id : null,
    };
  } catch {
    return null;
  }
}

/** Cast / flip / undo a vote. `value=0` undoes a prior vote. */
export async function votePiece(id: string, value: -1 | 0 | 1): Promise<VoteResponse | null> {
  if (!API_BASE || !id) return null;
  try {
    const res = await fetch(`${API_BASE}/pieces/${encodeURIComponent(id)}/vote`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ value }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as {
      piece_id?: string;
      likes?: number;
      dislikes?: number;
      my_vote?: number;
    };
    if (typeof data.piece_id !== "string") return null;
    const mv = data.my_vote === -1 || data.my_vote === 0 || data.my_vote === 1 ? data.my_vote : 0;
    return {
      pieceId: data.piece_id,
      likes: typeof data.likes === "number" ? data.likes : 0,
      dislikes: typeof data.dislikes === "number" ? data.dislikes : 0,
      myVote: mv as -1 | 0 | 1,
    };
  } catch {
    return null;
  }
}

export function providerLabel(p: Provider): string {
  switch (p.kind) {
    case "oracle":
      return p.agentId ? `Generated by ${p.agentId} via MeshKore Oracle` : "MeshKore Oracle";
    case "openrouter":
      return "OpenRouter (fallback)";
    case "pollinations":
      return "Pollinations (fallback)";
    case "pollinations-stub":
      return "Pollinations (dev stub)";
  }
}
